import React from 'react';
import {Map, List} from 'immutable';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import {
  simplifySkuValues,
  getProductOptionValueName,
} from '@packages/shared/site';
import {
  Plugins,
  withSystem,
  Atom,
  ChildRender,
  ContextType,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {TListOfElement, TRecord} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  Property,
  PropertyEnum,
  PropTable,
} from '@packages/systems/commerce/core';
import {useRuntimeEnvironment, useTemplate} from '@packages/systems/render';
import {
  getChildren,
  getInPojo,
  getString,
  getStringOrElse,
} from '@packages/utilities/data-utils';

const {SSR, EXPORT} = WebflowEnvironments;

const createTemplateId = (id: string) => `wf-template-${id}`;

const renderOption =
  ({
    renderChild,
    optionValues,
    children,
    context,
  }: {
    renderChild: ChildRender;
    optionValues: Record<string, string>;
    children: unknown[];
    context: ContextType;
  }) =>
  (
    option:
      | Property
      | {
          enum: Array<PropertyEnum>;
          id: string;
          name: string;
        },
    itemIndex: number
  ) => {
    const childKey =
      getString(getInPojo(option, ['id'])) || 'option-' + itemIndex;
    const optionValueName = getProductOptionValueName(option, optionValues);
    // TODO (ecommerce): Memoize commerce context mutations
    const newContext = context
      .set('data', {
        name: getStringOrElse(getInPojo(option, ['name']), ''),
        value: optionValueName,
      })
      // Due to limitations of migrations not reaching s3, we need to keep these values
      // within the context because we can't retroactively change these Atoms to rely
      // solely on dynamic bindings.
      .set('label', getStringOrElse(getInPojo(option, ['name']), ''))
      .set('value', optionValueName)
      // @ts-expect-error - TS7006 - Parameter 'bindingPathPrefix' implicitly has an 'any' type.
      .updateIn(['bindingPathPrefix'], [], (bindingPathPrefix) =>
        bindingPathPrefix.concat(['product', 'f_sku_properties_3dr[]'])
      );

    return children.map((child) =>
      renderChild(child, newContext, childKey, itemIndex)
    );
  };

const CommerceCartOptionList = Atom.createRender((prevProps) => {
  const {
    attributes,
    classes,
    context,
    data,
    generateAttributes,
    elementId,
    optionSets,
    optionValues,
    renderChild,
  } = prevProps;

  const runtimeEnvironment = useRuntimeEnvironment();

  if (optionValues && optionSets) {
    const renderOptionWithContext = renderOption({
      renderChild,
      optionValues: simplifySkuValues(optionValues),
      children: getChildren(data),
      context,
    });

    const renderOptions = (currentOptionSets: PropTable) =>
      currentOptionSets.length > 0
        ? currentOptionSets.map(renderOptionWithContext)
        : null;

    if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
      const tagAttributes = {
        [WF_COLLECTION_DATA_KEY]: encodeURIComponent(
          `database.commerceOrder.userItems[].product.f_sku_properties_3dr`
        ),
        [WF_TEMPLATE_ID_DATA_KEY]: createTemplateId(elementId),
      } as const;
      const newAttributes = generateAttributes(attributes, classes);
      const oldWfBindings = newAttributes['data-wf-bindings'];
      newAttributes['data-wf-bindings'] =
        typeof oldWfBindings === 'string'
          ? oldWfBindings.replace(
              'f_sku_properties_3dr',
              'f_sku_properties_3dr[]'
            )
          : oldWfBindings;
      const content = renderOptions(optionSets);
      const template = useTemplate();
      const templateContent = template(content);

      return (
        <>
          <script type={WF_TEMPLATE_TYPE} id={createTemplateId(elementId)}>
            {templateContent}
          </script>
          <ul {...newAttributes} {...tagAttributes}>
            {content}
          </ul>
        </>
      );
    }

    return (
      <ul {...generateAttributes(attributes, classes)}>
        {renderOptions(optionSets)}
      </ul>
    );
  }
  return null;
});

const staticBindings = Map({
  optionSets: List([
    Map({
      slug: 'product',
      type: 'ItemRef',
    }),
    Map({
      slug: 'sku-properties',
      type: 'CommercePropTable',
    }),
  ]),
  optionValues: List([
    Map({
      slug: 'sku',
      type: 'ItemRef',
    }),
    Map({
      slug: 'sku-values',
      type: 'CommercePropValues',
    }),
  ]),
});

const config = createIntegratedAtom({
  showEmpty: false,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  displayName: 'Option List',
  constraints: {
    wraps: ['CommerceCartOptionListItem'],
    ancestors: [{is: 'CommerceCartItem', rule: 'ExactlyOne'}],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          textDecoration: 'none',
          paddingLeft: px(0),
          marginBottom: px(0),
          listStyleType: 'none',
        },
      },
    },
  },
  render: CommerceCartOptionList,
  staticBindings,
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    children: TListOfElement,
  },
});

export default config;
